import type { ReactNode } from "react";

import type { Chip } from "../ride-journey.ts";

/**
 * The frame every screen in `10 Rider App.dc.html` is drawn in, built once.
 *
 * The design is one page body with eleven sets of values passed through it: the status bar, the hero
 * with its app bar, and the sheet with its handle, head, fields, option rows, fare box, key/value
 * rows, driver card, stars, note and action group are the same markup on every screen, and only the
 * `SC` table differs. So this is one component rather than nine, and a difference between two
 * screens is a difference in the data rather than in the markup.
 *
 * The DOM below is the design's own, element for element and in its order:
 *
 *     screen
 *       top                     the hero: a schematic map or an illustration
 *         [map | hero]
 *         appBar                back, the CityCab mark, and the person/SOS control
 *         [staleness]
 *       sheet
 *         handle
 *         head                  chip, then title and sub
 *         …                     the screen's own middle, in the design's order
 *         actions               the 52dp buttons, pinned to the floor
 *
 * ## The one row that is not here, and why
 *
 * The design's first row inside `screen` is the device's status bar: a clock, a signal glyph and a
 * battery glyph. A web page cannot draw that row from anything it knows — the signal strength and
 * the battery level are not published to a document on the browsers this product supports, and
 * drawing them from nothing is the one thing these rules forbid without qualification. The clock
 * could be drawn and would then sit alone in a band whose other half was invented, which is worse
 * than a band this application does not claim.
 *
 * So the application starts at the hero. The acceptance check hides that row on the design side and
 * shortens the design's own `screen` by exactly its measured height, so the comparison is of the
 * same geometry rather than nearly it. This is the same treatment `apps/driver-app` arrived at, and
 * it is recorded there as R-29 and Q-58; nothing on the rider's status bar is lost beyond the clock,
 * because the design draws no green location dot on this channel.
 */

/** The app bar's left control: `menu` on the first screen, `arrow_back` on every other. */
export interface BackControl {
  readonly glyph: "menu" | "arrow_back";
  readonly label: string;
  readonly onPress?: () => void;
}

/**
 * The app bar's right control, which is a person glyph until a driver is assigned and then a red
 * SOS.
 *
 * The design's own note: _"From the moment a driver is assigned, the top-right control becomes a red
 * SOS (SAF-01), on every trip screen."_ So `armed` is driven by the journey's stage rather than by
 * which screen is open — a rider on `8 Complete` after a ride that is still unknown must not lose
 * the control, and a rider on `3 Quote` with no driver yet must not be shown one.
 *
 * `unavailable` is the reason pressing it cannot work, when there is one. SAF-01's incident is
 * addressed by a `tripId`, and R-28 says a rider cannot get one — so on a journey with
 * `tripAbsence: not_joinable` the red control is drawn, because the design draws it and nothing may
 * be removed, and pressing it says why instead of appearing to work. That is the sharpest
 * consequence of R-28 in this whole application and it is deliberately visible.
 */
export interface SafetyControl {
  readonly armed: boolean;
  readonly onPress?: () => void;
  readonly unavailable?: string;
}

/** One 52dp action. The design's `btns`, where `primary` is its `p` flag. */
export interface ActionProps {
  readonly label: string;
  readonly glyph: string;
  readonly primary: boolean;
  readonly onPress?: () => void;
  /**
   * Why this action cannot be carried out, when the platform publishes no route for it.
   *
   * The button is still drawn, because the design draws it and nothing may be removed. Pressing it
   * says this instead of appearing to work. Three of the designed buttons are in this state:
   * `Schedule a ride` needs a scheduling field MOB-03's request body does not have,
   * `Question this fare` needs a support-case route that exists only under `/internal/v1`, and
   * `Share trip` needs the `tripId` R-28 says a rider cannot get.
   */
  readonly unavailable?: string;
}

export interface PhoneShellProps {
  /** The screen family's own class, which anchors its stylesheet: `where`, `choose`, `journey`. */
  readonly screen: string;
  /** The hero: a `<MapHero>`, a `<Hero>`, or nothing at all on a state screen. */
  readonly hero?: ReactNode;
  readonly back?: BackControl;
  readonly safety?: SafetyControl;
  readonly chip: Chip;
  readonly title: string;
  readonly sub: string;
  /** Said under the sub-line when the words on this screen are this build's rather than the design's. */
  readonly flag?: string;
  /** The screen's own middle, composed in the design's own order by the screen itself. */
  readonly children?: ReactNode;
  readonly actions?: readonly ActionProps[];
}

export function PhoneShell(props: PhoneShellProps): ReactNode {
  return (
    <div className={`screen ${props.screen}`}>
      <div className="screen__top">
        {props.hero}
        <div className="screen__appbar">
          {props.back === undefined ? (
            <span />
          ) : (
            <button
              type="button"
              className="appbar__icon"
              aria-label={props.back.label}
              onClick={props.back.onPress}
            >
              <span className="glyph" aria-hidden="true">
                {props.back.glyph}
              </span>
            </button>
          )}

          <span className="appbar__logo">
            <span className="appbar__mark" aria-hidden="true" />
            <span className="appbar__word">CityCab</span>
          </span>

          {props.safety === undefined ? <span /> : <SafetyButton {...props.safety} />}
        </div>
      </div>

      <div className="sheet">
        <span className="sheet__handle" />

        <div className="sheet__head">
          <StateChip chip={props.chip} />
          <div className="sheet__headings">
            <strong className="sheet__title">{props.title}</strong>
            <span className="sheet__sub">{props.sub}</span>
            {props.flag === undefined ? null : <span className="sheet__flag">{props.flag}</span>}
          </div>
        </div>

        {props.children}

        <div className="actions">
          {(props.actions ?? []).map((action) => (
            <Action key={action.label} {...action} />
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * The state chip: a glyph and a word, never a colour alone.
 *
 * `unknown` is the only class that renders text inside its glyph — a literal `?`, as the design's own
 * `chipQ` does — and it is the class this application reaches for whenever the platform could not
 * confirm something. The design itself puts it on `4 Confirming` as `not confirmed yet`, which is
 * the clearest statement in the whole bundle that an unconfirmed state has its own look rather than
 * borrowing a confident one.
 */
export function StateChip({ chip }: { readonly chip: Chip }): ReactNode {
  return (
    <span className={`chip chip--${chip.class}`}>
      <span className="chip__glyph" aria-hidden="true">
        {chip.class === "unknown" ? "?" : ""}
      </span>
      {chip.label}
    </span>
  );
}

/** One key/value row. `value: null` is an absence and is drawn as one. */
export interface RowProps {
  readonly key: string;
  readonly value: string | null;
  /** What to put in the absence's place, which is always a reason rather than a dash. */
  readonly because?: string;
}

/**
 * The design's `rowsBox`: a rounded card of key/value rows with hairlines between them.
 *
 * Used by `3 Quote`, `8 Complete` and `8b Fare differs`, and every one of them has at least one row
 * with no source — the masked payment instrument on two of them, the free-cancellation window and
 * the pricing policy on the third. So the absent case is not an edge here; it is most of what this
 * component draws today.
 */
export function Rows({ rows }: { readonly rows: readonly RowProps[] }): ReactNode {
  if (rows.length === 0) return null;
  return (
    <div className="rows">
      {rows.map((row) => (
        <div key={row.key} className="rows__row">
          <span className="rows__key">{row.key}</span>
          {row.value === null ? (
            <span className="absent" title={row.because}>
              {row.because ?? "Not published"}
            </span>
          ) : (
            <span className="rows__value">{row.value}</span>
          )}
        </div>
      ))}
    </div>
  );
}

/** The sheet's note: `info` is a blue card, `pending` is the dashed outline `4 Confirming` uses. */
export function Note({
  kind,
  text,
}: {
  readonly kind: "info" | "pending";
  readonly text: string;
}): ReactNode {
  return (
    <div className={`note note--${kind}`} role={kind === "pending" ? "status" : undefined}>
      <span className="glyph" aria-hidden="true">
        {kind === "pending" ? "sync" : "info"}
      </span>
      <span>{text}</span>
    </div>
  );
}

/**
 * A value the platform does not publish, at whatever scale its slot is.
 *
 * One component so that every absence on this application looks the same, which is the point: a
 * rider who has learned what the dashed pill means on one screen knows it on the next. `title`
 * carries the full reason where the slot is too small for it.
 */
export function Absent({
  words,
  className,
}: {
  readonly words: string;
  readonly className?: string;
}): ReactNode {
  return (
    <span className={className === undefined ? "absent" : `absent ${className}`} title={words}>
      {words}
    </span>
  );
}

/**
 * The red SOS, or the person glyph that precedes it.
 *
 * `aria-disabled` rather than `disabled` when it cannot work: a control a rider cannot reach with a
 * screen reader is worse than one that explains itself, and on a safety control that is not a close
 * call.
 */
function SafetyButton(control: SafetyControl): ReactNode {
  return (
    <button
      type="button"
      className={`appbar__sos${control.armed ? " appbar__sos--active" : ""}`}
      aria-label={control.armed ? "Emergency help" : "Your account"}
      {...(control.unavailable === undefined
        ? {}
        : { "aria-disabled": true, title: control.unavailable })}
      onClick={control.unavailable === undefined ? control.onPress : undefined}
    >
      {control.armed ? (
        "SOS"
      ) : (
        <span className="glyph" aria-hidden="true">
          person
        </span>
      )}
    </button>
  );
}

function Action(action: ActionProps): ReactNode {
  return (
    <button
      type="button"
      className={`action${action.primary ? " action--primary" : ""}`}
      {...(action.unavailable === undefined
        ? {}
        : { "aria-disabled": true, title: action.unavailable })}
      onClick={action.unavailable === undefined ? action.onPress : undefined}
    >
      <span className="glyph" aria-hidden="true">
        {action.glyph}
      </span>
      {action.label}
    </button>
  );
}
